import React, { FC, useEffect, useMemo, useState } from 'react';
import { Exercise, ExerciseType } from '../../../../config/api/models/Exercise';
import { Block } from '../../../../config/api/models/Block';
import { useFetch, useTrustedMessageListener, useMessageReceivedFromOrigin } from '../../hooks';
import { LTIHiddenForm } from './LTIHiddenForm';
import { ltiResponseMapper, ltiSettingsByType } from './constants';
import { Message } from '../forms';
import { useAppDispatch, useAppSelector } from '../../hooks/useAppDispatch';
import { clearExerciseToDuplicate, updateBlock } from '../../slices/blocks/entity/BlocksSlice';
import { isJson } from '../../helpers/json';

const H5P_FRAME_RESIZE_EVENT = 'lti.frameResize';

type LTIProps = {
  type: ExerciseType;
  exercise: Exercise | Block;
  isVisible: boolean;
  onFrameResize?: (height: number) => void;
};

export const LTI: FC<LTIProps> = ({ type, exercise, isVisible, onFrameResize }) => {
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [getLtiResponse, getLti] = useFetch('', {
    mapper: ltiResponseMapper,
  });
  const exerciseToDuplicate = useAppSelector((state) => state.blocks.exerciseToDuplicate);
  const dispatch = useAppDispatch();

  const target = useMemo(() => `iframe_exercise_${exercise.id}`, [exercise.id]);
  const action: string = useMemo(() => {
    if (ltiSettingsByType[type] === undefined || !ltiSettingsByType[type].url) {
      return;
    }

    if (getLtiResponse.success && getLtiResponse.data['launch_presentation_return_url'])
      return getLtiResponse.data['launch_presentation_return_url'];

    return ltiSettingsByType[type].url;
  }, [getLtiResponse, type]);

  useMessageReceivedFromOrigin<string>(target, 'https://wezoozacademy.h5p.com', (message) => {
    if (!onFrameResize) return;
    if (typeof message !== 'string') return;

    const { isJson: isValidJson, json } = isJson(message);
    if (!isValidJson) return;

    if (json.subject === H5P_FRAME_RESIZE_EVENT) onFrameResize(json.height);
  });

  useTrustedMessageListener<{ exerciseId: string; exerciseURL: string }>((data) => {
    if (data.exerciseId !== exercise.id) return;
    if (ltiSettingsByType[type] === undefined) return;

    const block = exercise as Block;
    dispatch(updateBlock({ ...block, url: data.exerciseURL }));
    getLti({
      path: ltiSettingsByType[type]?.apiRoute(exercise.id, false),
    });
  });

  useEffect(() => {
    if (ltiSettingsByType[type] === undefined) return;
    const duplicationApiRoute = ltiSettingsByType[type]?.duplicationApiRoute;

    if (duplicationApiRoute && exerciseToDuplicate && exerciseToDuplicate.newId === exercise.id) {
      getLti({
        path: duplicationApiRoute(exerciseToDuplicate.newId, exerciseToDuplicate.originalUrl),
      });
      setIsDuplicating(true);
      dispatch(clearExerciseToDuplicate());
    } else {
      if (isDuplicating || !isVisible) return;
      const shouldDeepLink = exercise.hasOwnProperty('url') && !(exercise as Exercise).url;
      getLti({
        path: ltiSettingsByType[type]?.apiRoute(exercise.id, shouldDeepLink),
      });
    }
  }, [
    exerciseToDuplicate,
    exercise,
    getLti,
    getLtiResponse.initial,
    dispatch,
    isDuplicating,
    type,
    isVisible,
  ]);

  useEffect(() => {
    return () => {
      /*
        LTI sends results of exercises on an unload event of the iframe.
        React's removing of the iframe's parent component doesn't trigger the unload event.
        To solve this, we force the removal of the iframe element before the component unmounts.
        In addition, the src attribute of the iframe is altered, to help trigger the unload.
      */
      const iframe: HTMLIFrameElement | null = document.getElementById(target) as HTMLIFrameElement;

      if (iframe) {
        iframe.setAttribute('src', process.env.PUBLIC_URL);
        iframe.parentElement!.removeChild(iframe);
      }
    };
  }, [target]);

  useEffect(() => {
    if (getLtiResponse.success && exerciseToDuplicate && exerciseToDuplicate.newId === exercise.id)
      setIsDuplicating(false);
  }, [getLtiResponse.success, exerciseToDuplicate, exercise]);

  if (!isVisible) return null;

  return (
    <>
      {getLtiResponse.error && <Message type="error">{getLtiResponse.reason}</Message>}
      {!getLtiResponse.pending && getLtiResponse.success && (
        <LTIHiddenForm
          isDuplicate={!!exerciseToDuplicate && exercise.id === exerciseToDuplicate.newId}
          exerciseId={exercise.id}
          target={target}
          action={action}
          fields={getLtiResponse.data}
        />
      )}
      {getLtiResponse.success && (
        <iframe
          title={`${type.toUpperCase()} portal to exercise ${exercise.id}`}
          id={target}
          name={target}
          width="100%"
          height="100%"
          src="about:blank"
          style={{ border: 0 }}
          allow="geolocation *; microphone *; camera *; midi *; encrypted-media *"
        />
      )}
    </>
  );
};
